import React, { useState, useEffect } from "react";
import {
  Form,
  Tabs,
  Tab,
  Button,
  Dropdown,
  ListGroup,
  Row,
  Col,
  InputGroup,
  Card,
} from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import InputColor from "react-input-color";
import { X } from "react-feather";
import Select from "react-select";
import Preview from "./Preview";
import QRPreview from "./QRPreview";
const SurveyEditor = ({
  editingSurvey,
  setEditingSurvey,
  questions,
  setQuestions,
  branding,
  setBranding,
  settings,
  setSettings,
  globalSettings,
  setGlobalSettings,
  qrCodeSettings,
  setQrCodeSettings,
  conditionalLogic,         // ✅ use props
  setConditionalLogic,      // ✅ use props
  surveyLink,
  saveSurvey,
  stopEditing,
  fetchGlobalSettings,
  saveGlobalSettings,
  addQuestion,
  updateQuestion,
  removeQuestion,
  onDragEnd,
  renderQuestionPreview,
  handleLogoUrlChange,
  handleBackgroundChange,
  handleBackgroundValueChange,
  handleColorChange,
  handleTitleFontSizeChange,
  handleConditionalChange,
  handleQRSettingChange,
  previewDevice,
  setPreviewDevice,
  getBackgroundStyle,
  isDownloadOpen,
  setIsDownloadOpen,
  downloadQRPDF,
  downloadQRPNG,
  generateQRCanvas,
  activeKey,
  onSelect,
}) => {
  // Sync local state with qrCodeSettings on mount and when qrCodeSettings changes
  const [localQrTitle, setLocalQrTitle] = useState(qrCodeSettings.qrTitle || "");
  const [localQrTitleColor, setLocalQrTitleColor] = useState(qrCodeSettings.qrTitleColor || "#000000");
  const [localQrTitleSize, setLocalQrTitleSize] = useState(qrCodeSettings.qrTitleSize || 24);
  const [localQrDesc, setLocalQrDesc] = useState(qrCodeSettings.qrDesc || "");
  const [localQrDescColor, setLocalQrDescColor] = useState(qrCodeSettings.qrDescColor || "#000000");
  const [localQrDescSize, setLocalQrDescSize] = useState(qrCodeSettings.qrDescSize || 16);
  const [localHeaderLogo, setLocalHeaderLogo] = useState(qrCodeSettings.headerLogo || "");
  const [localBorderGradientStart, setLocalBorderGradientStart] = useState(qrCodeSettings.borderGradientStart || "#007bff");
  const [localBorderGradientEnd, setLocalBorderGradientEnd] = useState(qrCodeSettings.borderGradientEnd || "#66b2ff");
  const [localHeaderLogoWidth, setLocalHeaderLogoWidth] = useState(qrCodeSettings.headerLogoWidth || 100);
  const [localHeaderLogoHeight, setLocalHeaderLogoHeight] = useState(qrCodeSettings.headerLogoHeight || 40);
  useEffect(() => {
    setLocalQrTitle(qrCodeSettings.qrTitle || "");
    setLocalQrTitleColor(qrCodeSettings.qrTitleColor || "#000000");
    setLocalQrTitleSize(qrCodeSettings.qrTitleSize || 24);
    setLocalQrDesc(qrCodeSettings.qrDesc || "");
    setLocalQrDescColor(qrCodeSettings.qrDescColor || "#000000");
    setLocalQrDescSize(qrCodeSettings.qrDescSize || 16);
    setLocalHeaderLogo(qrCodeSettings.headerLogo || "");
    setLocalBorderGradientStart(qrCodeSettings.borderGradientStart || "#007bff");
    setLocalBorderGradientEnd(qrCodeSettings.borderGradientEnd || "#66b2ff");
    setLocalHeaderLogoWidth(qrCodeSettings.headerLogoWidth || 100);
    setLocalHeaderLogoHeight(qrCodeSettings.headerLogoHeight || 40);
  }, [qrCodeSettings]);
  const handleSaveSurvey = () => {
    const updatedQrCodeSettings = {
      qrTitle: localQrTitle,
      qrTitleColor: localQrTitleColor,
      qrTitleSize: localQrTitleSize,
      qrDesc: localQrDesc,
      qrDescColor: localQrDescColor,
      qrDescSize: localQrDescSize,
      headerLogo: localHeaderLogo,
      borderGradientStart: localBorderGradientStart,
      borderGradientEnd: localBorderGradientEnd,
      fgColor: qrCodeSettings.fgColor || "#000000",
      bgColor: qrCodeSettings.bgColor || "#ffffff",
      showHolderLogo: qrCodeSettings.showHolderLogo || false,
      headerLogoWidth: localHeaderLogoWidth,
      headerLogoHeight: localHeaderLogoHeight,
    };
     setQrCodeSettings(updatedQrCodeSettings);
    // ✅ save with conditionalLogic from props
    saveSurvey({
      ...editingSurvey,
      qrCodeSettings: updatedQrCodeSettings,
      conditionalLogic,
    });
  };
  const pageSizeOptions = [
    { value: '4-6', label: '4x6 inches' },
    { value: '5-6', label: '5x6 inches' },
    { value: '4-4', label: '4x4 inches' },
    { value: '3-3', label: '3x3 inches' },
     { value: '6-8', label: '6x8 inches' },
  ];
  return (
    <Row>
      <Col md={6}>
        <Tabs activeKey={activeKey} onSelect={onSelect} id="survey-builder-tabs" className="mb-3" aria-label="Survey Builder Tabs">
          <Tab eventKey="details" title="Survey Details">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Survey Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editingSurvey?.title || ""}
                  onChange={(e) => setEditingSurvey({ ...editingSurvey, title: e.target.value })}
                  aria-required="true"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title Color</Form.Label>
                <InputColor
                  initialValue={branding.titleColor}
                  onChange={(color) => handleColorChange("titleColor", color)}
                  placement="right"
                  aria-label="Title Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Title Font Size ({branding.titleFontSize}rem)</Form.Label>
                <Form.Control
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={branding.titleFontSize}
                  onChange={handleTitleFontSizeChange}
                  aria-label="Title Font Size Slider"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editingSurvey?.description || ""}
                  onChange={(e) => setEditingSurvey({ ...editingSurvey, description: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Thank You Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editingSurvey?.thankYouMessage || ""}
                  onChange={(e) => setEditingSurvey({ ...editingSurvey, thankYouMessage: e.target.value })}
                  placeholder="Enter thank you message"
                  aria-label="Thank You Message"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editingSurvey?.status || "Draft"}
                  onChange={(e) => setEditingSurvey({ ...editingSurvey, status: e.target.value })}
                  aria-required="true"
                >
                  <option>Active</option>
                  <option>Draft</option>
                  <option>Archived</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Allow Anonymous Responses"
                  checked={settings.anonymous}
                  onChange={(e) => setSettings({ ...settings, anonymous: e.target.checked })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Expiry Date (Optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={settings.expiry}
                  onChange={(e) => setSettings({ ...settings, expiry: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Tab>
          <Tab eventKey="form" title="Form Builder">
            <h6 className="mb-3">Questions</h6>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <ListGroup {...provided.droppableProps} ref={provided.innerRef} aria-label="Question List">
                    {questions.map((q, index) => (
                      <Draggable key={q.id} draggableId={q.id.toString()} index={index}>
                        {(provided) => (
                          <ListGroup.Item
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="mb-2 p-2 d-flex align-items-center"
                          >
                            <div className="flex-grow-1 d-flex align-items-center gap-2">
                              <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm" aria-label="Question Type Dropdown">
                                  {q.type.charAt(0).toUpperCase() + q.type.slice(1)}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "text")}>Text</Dropdown.Item>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "rating")}>Rating</Dropdown.Item>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "nps")}>NPS</Dropdown.Item>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "multiple")}>Multiple Choice</Dropdown.Item>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "checkbox")}>Checkbox</Dropdown.Item>
                                  <Dropdown.Item onClick={() => updateQuestion(q.id, "type", "date")}>Date</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                              <Form.Control
                                type="text"
                                placeholder="Question Label"
                                value={q.label}
                                onChange={(e) => updateQuestion(q.id, "label", e.target.value)}
                                aria-label={`Question ${q.id} Label`}
                              />
                              <Form.Check
                                type="switch"
                                label="Required"
                                checked={q.required}
                                onChange={(e) => updateQuestion(q.id, "required", e.target.checked)}
                              />
                              <Form.Control
                                type="text"
                                placeholder="Placeholder/Hint"
                                value={q.placeholder}
                                onChange={(e) => updateQuestion(q.id, "placeholder", e.target.value)}
                                aria-label={`Question ${q.id} Placeholder`}
                              />
                              {q.type === "rating" && (
                                <Form.Select
                                  value={q.style}
                                  onChange={(e) => updateQuestion(q.id, "style", e.target.value)}
                                  aria-label={`Question ${q.id} Style`}
                                >
                                  <option value="star">Star</option>
                                  <option value="smiley">Smiley</option>
                                </Form.Select>
                              )}
                            </div>
                            <Button
                              variant="link"
                              className="text-danger"
                              onClick={() => removeQuestion(q.id)}
                              aria-label={`Remove Question ${q.id}`}
                            >
                              <X size={16} />
                            </Button>
                          </ListGroup.Item>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ListGroup>
                )}
              </Droppable>
            </DragDropContext>
            <Dropdown className="mt-3">
              <Dropdown.Toggle variant="outline-primary" aria-label="Add Question Dropdown">
                Add Question
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => addQuestion("text")}>Text</Dropdown.Item>
                <Dropdown.Item onClick={() => addQuestion("rating")}>Rating</Dropdown.Item>
                <Dropdown.Item onClick={() => addQuestion("nps")}>NPS</Dropdown.Item>
                <Dropdown.Item onClick={() => addQuestion("multiple")}>Multiple Choice</Dropdown.Item>
                <Dropdown.Item onClick={() => addQuestion("checkbox")}>Checkbox</Dropdown.Item>
                <Dropdown.Item onClick={() => addQuestion("date")}>Date</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Card className="mt-4">
              <Card.Header>Optional Fields</Card.Header>
              <Card.Body>
                <Form.Group className="d-flex flex-wrap gap-2">
                  <Form.Check
                    type="switch"
                    label="Show Phone"
                    checked={settings.showPhone}
                    onChange={(e) => setSettings({ ...settings, showPhone: e.target.checked })}
                    aria-label="Toggle Show Phone"
                  />
                  <Form.Check
                    type="switch"
                    label="Show Email"
                    checked={settings.showEmail}
                    onChange={(e) => setSettings({ ...settings, showEmail: e.target.checked })}
                    aria-label="Toggle Show Email"
                  />
                  <Form.Check
                    type="switch"
                    label="Show Birthday"
                    checked={settings.showBirthday}
                    onChange={(e) => setSettings({ ...settings, showBirthday: e.target.checked })}
                    aria-label="Toggle Show Birthday"
                  />
                  <Form.Check
                    type="switch"
                    label="Show Anniversary"
                    checked={settings.showAnniversary}
                    onChange={(e) => setSettings({ ...settings, showAnniversary: e.target.checked })}
                    aria-label="Toggle Show Anniversary"
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="branding" title="Branding">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Theme Color</Form.Label>
                <InputColor
                  initialValue={branding.themeColor}
                  onChange={(color) => handleColorChange("themeColor", color)}
                  placement="right"
                  aria-label="Theme Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Button Style</Form.Label>
                <Form.Select
                  value={branding.buttonStyle}
                  onChange={(e) => setBranding({ ...branding, buttonStyle: e.target.value })}
                  aria-label="Button Style"
                >
                  <option value="rounded">Rounded</option>
                  <option value="square">Square</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Show Logo in Form"
                  checked={branding.showLogo}
                  onChange={(e) => setBranding({ ...branding, showLogo: e.target.checked })}
                  aria-label="Toggle Logo in Form"
                />
              </Form.Group>
              {branding.showLogo && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Form Logo URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={branding.logoSrc}
                    onChange={handleLogoUrlChange}
                    placeholder="Enter logo URL"
                    aria-label="Form Logo URL"
                  />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Logo Width (px)</Form.Label>
                    <Form.Control
                      type="number"
                      value={branding.logoWidth || 100}
                      onChange={(e) => setBranding({ ...branding, logoWidth: parseInt(e.target.value) })}
                      min="50"
                      max="200"
                      aria-label="Logo Width"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Logo Height (px)</Form.Label>
                    <Form.Control
                      type="number"
                      value={branding.logoHeight || 40}
                      onChange={(e) => setBranding({ ...branding, logoHeight: parseInt(e.target.value) })}
                      min="20"
                      max="100"
                      aria-label="Logo Height"
                    />
                  </Form.Group>
                </>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Background Style</Form.Label>
                <Form.Select
                  value={branding.backgroundType}
                  onChange={(e) => handleBackgroundChange(e.target.value)}
                  aria-label="Background Style"
                >
                  <option value="color">Color</option>
                  <option value="image">Image</option>
                  <option value="gradient">Gradient</option>
                </Form.Select>
              </Form.Group>
              {branding.backgroundType === "color" && (
                <Form.Group className="mb-3">
                  <Form.Label>Background Color</Form.Label>
                  <InputColor
                    initialValue={branding.backgroundValue}
                    onChange={(color) => handleBackgroundValueChange(color)}
                    placement="right"
                    aria-label="Background Color Picker"
                  />
                </Form.Group>
              )}
              {branding.backgroundType === "image" && (
                <Form.Group className="mb-3">
                  <Form.Label>Background Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={branding.backgroundValue}
                    onChange={(e) => handleBackgroundValueChange({ hex: e.target.value })}
                    placeholder="Enter image URL"
                    aria-label="Background Image URL"
                  />
                </Form.Group>
              )}
              {branding.backgroundType === "gradient" && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Gradient Start Color</Form.Label>
                    <InputColor
                      initialValue={branding.gradientStart}
                      onChange={(color) => handleBackgroundValueChange({ start: color })}
                      placement="right"
                      aria-label="Gradient Start Color Picker"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Gradient End Color</Form.Label>
                    <InputColor
                      initialValue={branding.gradientEnd}
                      onChange={(color) => handleBackgroundValueChange({ end: color })}
                      placement="right"
                      aria-label="Gradient End Color Picker"
                    />
                  </Form.Group>
                </>
              )}
            </Form>
          </Tab>
          {/* Conditional Logic Tab */}
          <Tab eventKey="conditional" title="Conditional Logic">
            <Form>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Enable Conditional Redirect"
                  checked={conditionalLogic?.enabled || false}
                  onChange={(e) => setConditionalLogic({ ...conditionalLogic, enabled: e.target.checked })}
                  aria-label="Enable Conditional Redirect"
                />
              </Form.Group>
              {conditionalLogic?.enabled && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Condition Based On</Form.Label>
                    <Form.Select
                      value={conditionalLogic?.basedOn || ""}
                      onChange={(e) => setConditionalLogic({ ...conditionalLogic, basedOn: e.target.value })}
                      aria-label="Condition Based On"
                    >
                      <option value="">Select</option>
                      <option value="overall">Overall Rating</option>
                      <option value="nps">NPS Score</option>
                      {questions.filter(q => q.type === "rating" || q.type === "nps").map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Operator</Form.Label>
                    <Form.Select
                      value={conditionalLogic?.operator || ">"}
                      onChange={(e) => setConditionalLogic({ ...conditionalLogic, operator: e.target.value })}
                      aria-label="Condition Operator"
                    >
                      <option value=">">Greater Than</option>
                      <option value="<">Less Than</option>
                      <option value="=">Equal To</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Value</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={conditionalLogic.basedOn === "nps" || conditionalLogic.basedOn === "overall" && questions.some(q => q.type === "nps") ? 10 : 5}
                      value={conditionalLogic?.value || ""}
                      onChange={(e) => setConditionalLogic({ ...conditionalLogic, value: parseInt(e.target.value) })}
                      placeholder="Enter value (e.g., 5)"
                      aria-label="Condition Value"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Message to Display</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={conditionalLogic?.message || ""}
                      onChange={(e) => setConditionalLogic({ ...conditionalLogic, message: e.target.value })}
                      placeholder="Enter message to show before redirection"
                      aria-label="Message to Display"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Redirect URL (Mandatory)</Form.Label>
                    <Form.Control
                      type="text"
                      value={conditionalLogic?.redirectUrl || ""}
                      onChange={(e) => setConditionalLogic({ ...conditionalLogic, redirectUrl: e.target.value })}
                      placeholder="Enter redirect URL"
                      aria-label="Redirect URL"
                      required
                    />
                  </Form.Group>
                </>
              )}
            </Form>
          </Tab>
          <Tab eventKey="qrCode" title="QR Code">
            <Form>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Show Holder Logo"
                  checked={qrCodeSettings.showHolderLogo}
                  onChange={(e) => handleQRSettingChange("showHolderLogo", e.target.checked)}
                  aria-label="Toggle Holder Logo"
                />
              </Form.Group>
              {qrCodeSettings.showHolderLogo && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Holder Logo URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={localHeaderLogo}
                      onChange={(e) => setLocalHeaderLogo(e.target.value)}
                      placeholder="Enter logo URL"
                      aria-label="Holder Logo URL"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Logo Width (px)</Form.Label>
                    <Form.Control
                      type="number"
                      value={localHeaderLogoWidth}
                      onChange={(e) => setLocalHeaderLogoWidth(parseInt(e.target.value))}
                      min="50"
                      max="200"
                      aria-label="Logo Width"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Logo Height (px)</Form.Label>
                    <Form.Control
                      type="number"
                      value={localHeaderLogoHeight}
                      onChange={(e) => setLocalHeaderLogoHeight(parseInt(e.target.value))}
                      min="20"
                      max="100"
                      aria-label="Logo Height"
                    />
                  </Form.Group>
                </>
              )}
              <Form.Group className="mb-3">
                <Form.Label>QR Title</Form.Label>
                <Form.Control
                  type="text"
                  value={localQrTitle}
                  onChange={(e) => setLocalQrTitle(e.target.value)}
                  aria-label="QR Title"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Title Color</Form.Label>
                <InputColor
                  initialValue={localQrTitleColor}
                  onChange={(color) => setLocalQrTitleColor(color.hex)}
                  placement="right"
                  aria-label="QR Title Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Title Size (pt)</Form.Label>
                <Form.Control
                  type="number"
                  value={localQrTitleSize}
                  onChange={(e) => setLocalQrTitleSize(parseInt(e.target.value))}
                  min="10"
                  max="36"
                  aria-label="QR Title Size"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={localQrDesc}
                  onChange={(e) => setLocalQrDesc(e.target.value)}
                  aria-label="QR Description"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Description Color</Form.Label>
                <InputColor
                  initialValue={localQrDescColor}
                  onChange={(color) => setLocalQrDescColor(color.hex)}
                  placement="right"
                  aria-label="QR Description Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Description Size (pt)</Form.Label>
                <Form.Control
                  type="number"
                  value={localQrDescSize}
                  onChange={(e) => setLocalQrDescSize(parseInt(e.target.value))}
                  min="8"
                  max="24"
                  aria-label="QR Description Size"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Page Size</Form.Label>
                <Select
                  value={pageSizeOptions.find(opt => opt.value === qrCodeSettings.pageSize)}
                  onChange={(selected) => handleQRSettingChange('pageSize', selected.value)}
                  options={pageSizeOptions}
                  placeholder="Select page size"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Border Gradient Start Color</Form.Label>
                <InputColor
                  initialValue={localBorderGradientStart}
                  onChange={(color) => setLocalBorderGradientStart(color.hex)}
                  placement="right"
                  aria-label="Border Gradient Start Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Border Gradient End Color</Form.Label>
                <InputColor
                  initialValue={localBorderGradientEnd}
                  onChange={(color) => setLocalBorderGradientEnd(color.hex)}
                  placement="right"
                  aria-label="Border Gradient End Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Foreground Color</Form.Label>
                <InputColor
                  initialValue={qrCodeSettings.fgColor}
                  onChange={(color) => handleQRSettingChange("fgColor", color.hex)}
                  placement="right"
                  aria-label="QR Foreground Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>QR Background Color</Form.Label>
                <InputColor
                  initialValue={qrCodeSettings.bgColor}
                  onChange={(color) => handleQRSettingChange("bgColor", color.hex)}
                  placement="right"
                  aria-label="QR Background Color Picker"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Survey Link</Form.Label>
                <InputGroup>
                  <Form.Control type="text" value={surveyLink} readOnly aria-label="Survey Link" />
                  <Button
                    variant="outline-secondary"
                    aria-label="Copy Survey Link"
                    onClick={() => navigator.clipboard?.writeText(surveyLink)}
                  >
                    Copy
                  </Button>
                </InputGroup>
              </Form.Group>
            </Form>
          </Tab>
          <Tab eventKey="settings" title="Settings">
            <Form>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Email Alerts</h6>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Enable Email Alerts"
                      checked={settings.alertsEnabled || false}
                      onChange={(e) => setSettings({ ...settings, alertsEnabled: e.target.checked })}
                      aria-label="Email Alerts Toggle"
                    />
                  </Form.Group>
                  {settings.alertsEnabled && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Condition Based On</Form.Label>
                        <Form.Select
                          value={settings.alertBasedOn || ""}
                          onChange={(e) => setSettings({ ...settings, alertBasedOn: e.target.value })}
                          aria-label="Alert Condition Based On"
                        >
                          <option value="">Select</option>
                          <option value="overall">Overall Rating</option>
                          <option value="nps">NPS Score</option>
                          {questions.filter(q => q.type === "rating" || q.type === "nps").map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Operator</Form.Label>
                        <Form.Select
                          value={settings.alertOperator || ">"}
                          onChange={(e) => setSettings({ ...settings, alertOperator: e.target.value })}
                          aria-label="Alert Condition Operator"
                        >
                          <option value=">">Greater Than</option>
                          <option value="<">Less Than</option>
                          <option value="=">Equal To</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Value</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max={settings.alertBasedOn === "nps" || settings.alertBasedOn === "overall" && questions.some(q => q.type === "nps") ? 10 : 5}
                          value={settings.alertValue || ""}
                          onChange={(e) => setSettings({ ...settings, alertValue: parseInt(e.target.value) })}
                          placeholder="Enter value (e.g., 5)"
                          aria-label="Alert Condition Value"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Recipient Emails (up to 3, comma-separated)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="email1@example.com, email2@example.com"
                          value={settings.alertRecipients || ""}
                          onChange={(e) => {
                            const emails = e.target.value.split(',').map(em => em.trim()).slice(0, 3).join(', ');
                            setSettings({ ...settings, alertRecipients: emails });
                          }}
                          aria-label="Alert Recipient Emails"
                        />
                      </Form.Group>
                    </>
                  )}
                </Card.Body>
              </Card>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Feedback Digest</h6>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      label="Enable Feedback Digest"
                      checked={settings.digestEnabled || false}
                      onChange={(e) => setSettings({ ...settings, digestEnabled: e.target.checked })}
                      aria-label="Feedback Digest Toggle"
                    />
                  </Form.Group>
                  {settings.digestEnabled && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Digest Frequency</Form.Label>
                        <Form.Select
                          value={settings.digestFrequency || "daily"}
                          onChange={(e) => setSettings({ ...settings, digestFrequency: e.target.value })}
                          aria-label="Digest Frequency"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Recipient Emails (up to 3, comma-separated)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="email1@example.com, email2@example.com"
                          value={settings.digestRecipients || ""}
                          onChange={(e) => {
                            const emails = e.target.value.split(',').map(em => em.trim()).slice(0, 3).join(', ');
                            setSettings({ ...settings, digestRecipients: emails });
                          }}
                          aria-label="Digest Recipient Emails"
                        />
                      </Form.Group>
                    </>
                  )}
                </Card.Body>
              </Card>
              <Form.Group className="mb-3">
                <Form.Label>Access Control</Form.Label>
                <Form.Select
                  value={settings.access}
                  onChange={(e) => setSettings({ ...settings, access: e.target.value })}
                  aria-label="Access Control"
                >
                  <option>Public</option>
                  <option>Internal Only</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Language</Form.Label>
                <Form.Select
                  value={settings.language}
                  onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                  aria-label="Language"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Tab>
        </Tabs>
        <div className="d-flex justify-content-end mt-4">
          <Button variant="secondary" onClick={stopEditing} className="me-2 px-4 py-2" aria-label="Cancel">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSurvey} className="px-4 py-2" aria-label="Save Survey">
            Save Survey
          </Button>
        </div>
      </Col>
      <Col md={6} style={{ position: "sticky", top: "20px", alignSelf: "flex-start" }}>
        {activeKey === "qrCode" ? (
            <QRPreview
            qrCodeSettings={qrCodeSettings}
            surveyLink={surveyLink}
            branding={branding}
            isDownloadOpen={isDownloadOpen}
            setIsDownloadOpen={setIsDownloadOpen}
            downloadQRPDF={downloadQRPDF}
            downloadQRPNG={downloadQRPNG}
            qrTitle={localQrTitle}
            qrTitleColor={localQrTitleColor}
            qrTitleSize={localQrTitleSize}
            qrDesc={localQrDesc}
            qrDescColor={localQrDescColor}
            qrDescSize={localQrDescSize}
            headerLogo={localHeaderLogo}
            borderGradientStart={localBorderGradientStart}
            borderGradientEnd={localBorderGradientEnd}
            headerLogoWidth={localHeaderLogoWidth}
            headerLogoHeight={localHeaderLogoHeight}
          />
        ) : (
            <Preview
            previewDevice={previewDevice}
            setPreviewDevice={setPreviewDevice}
            branding={branding}
            editingSurvey={editingSurvey}
            questions={questions}
            renderQuestionPreview={renderQuestionPreview}
            getBackgroundStyle={getBackgroundStyle}
          />
        )}
      </Col>
    </Row>
  );
};
export default SurveyEditor;